import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthSelector } from "../../services/selectors/authSelector";
import {
  cancelPharmacistDriverVideoCallFromDeliveryApp,
  endPharmacistDriverVideoCallFromDeliveryApp,
  endPharmacistDriverVideoCallFromDeliveryAppOnUnload,
  fetchPharmacistDriverCallDrivers,
  initiatePharmacistDriverVideoCallFromDeliveryApp,
} from "./deliveryVideoCallApi";
import {
  subscribeToDeliveryVideoCallNotifications,
  subscribeToPharmacistDriverVideoCallSession,
} from "./videoCallHelper";
import Cookies from "js-cookie";
import { apiKey as ablyApiKey } from "../../networking/urlEndPoints";
import VideoCall from "./VideoCall";
import VideoCallErrorBoundary from "./VideoCallErrorBoundary";
import fallbackAvatar from "../../assets/images/userimg.png";
import { LoadingView } from "../common/loader";
import AnimatedBottomSheet from "../common/animatedBottomSheet";

const RING_TIMEOUT_MS = 30000;

const isFlutterNativeShell = () => {
  if (typeof window === "undefined") return false;
  const query = window.location?.search || "";
  try {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        window.DriverApp ||
        query.includes("flutter_app=1") ||
        window.sessionStorage?.getItem("flutter_app") === "1"
    );
  } catch (error) {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        window.DriverApp ||
        query.includes("flutter_app=1")
    );
  }
};

const postToFlutter = (payload) => {
  if (
    typeof window === "undefined" ||
    !window.DriverApp ||
    typeof window.DriverApp.postMessage !== "function"
  ) {
    return false;
  }
  try {
    window.DriverApp.postMessage(JSON.stringify(payload));
    return true;
  } catch (error) {
    return false;
  }
};

const normalizeCallData = (data) => {
  if (!data) return null;

  return {
    callId: data.call_id ?? data.callId,
    pharmacyId: data.pharmacy_id ?? data.pharmacyId,
    driverId: data.driver?.id ?? data.driver_id ?? data.driverId,
    driverName: data.driver?.name ?? data.driver_name ?? data.driverName,
    pharmacistId: data.pharmacist?.id ?? data.pharmacist_id ?? data.pharmacistId,
    pharmacistName: data.pharmacist?.name ?? data.pharmacist_name ?? data.pharmacistName,
    status: data.status,
    vonage_session_id: data.vonage_session_id,
    vonage_token: data.vonage_token,
    application_id: data.application_id,
    // Provider pinned by the backend; MediTalk calls also carry livekit_url.
    video_provider: data.video_provider,
    livekit_url: data.livekit_url,
    flow: data.flow,
  };
};

const getEventCallId = (payload) => payload?.call_id ?? payload?.callId;
const getEventStatus = (payload) => payload?.terminal_status ?? payload?.call_status ?? payload?.status;

const normalizeDrivers = (payload) => {
  const list =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.drivers) && payload.drivers) ||
    (Array.isArray(payload?.data) && payload.data) ||
    [];

  const seen = new Set();

  return list
    .map((item) => {
      const driver = item?.delivery_driver || item?.deliveryDriver || item?.driver || item;
      const id = driver?.id ?? item?.delivery_driver_id ?? item?.driver_id;
      const name = driver?.name || driver?.driver_name || item?.driver_name || `Assistant ${id || ""}`.trim();
      const email = driver?.email || item?.driver_email || "";
      const photo = driver?.user_photo || driver?.photo || driver?.avatar || "";

      return { id, name, email, photo };
    })
    .filter((driver) => {
      if (driver.id == null || seen.has(String(driver.id))) return false;
      seen.add(String(driver.id));
      return true;
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const PharmacistDriverCallLauncher = ({ trigger }) => {
  const {
    pharmacyId,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
  } = useAuthSelector();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState("idle");

  const timeoutRef = useRef(null);
  const activeCallRef = useRef(null);
  const callStateRef = useRef("idle");
  const unloadCleanupSentRef = useRef(false);
  const sessionEndSubscriptionRef = useRef(null);

  const canUseLauncher = Boolean(
    isPharmacistForSelectedPharmacy &&
    pharmacyId &&
    pharmacistIdForSelectedPharmacy
  );

  useEffect(() => {
    activeCallRef.current = activeCall;
    unloadCleanupSentRef.current = false;
  }, [activeCall]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const loadDrivers = useCallback(async () => {
    if (!pharmacyId) return;

    setLoadingDrivers(true);
    setNotice("");

    try {
      const response = await fetchPharmacistDriverCallDrivers(pharmacyId);
      setDrivers(normalizeDrivers(response));
    } catch (error) {
      setNotice("Unable to load assistants right now.");
    } finally {
      setLoadingDrivers(false);
    }
  }, [pharmacyId]);

  const openPicker = useCallback(() => {
    if (!canUseLauncher || activeCallRef.current) return;
    setNotice("");
    setDrivers([]);
    setSelectedDriverId("");
    setIsPickerOpen(true);
    loadDrivers();
  }, [canUseLauncher, loadDrivers]);

  useEffect(() => {
    if (!canUseLauncher) return undefined;

    const ably = subscribeToDeliveryVideoCallNotifications({
      pharmacyId,
      onEvent: (payload) => {
        if (payload?.type !== "pharmacist_driver_video_call_status") return;
        if (payload.pharmacist_id != null && String(payload.pharmacist_id) !== String(pharmacistIdForSelectedPharmacy)) return;

        const current = activeCallRef.current;
        const eventCallId = getEventCallId(payload);
        const eventStatus = getEventStatus(payload);
        if (!current?.callId || String(eventCallId) !== String(current.callId)) return;

        if (eventStatus === "accepted") {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setCallState("accepted");
          setActiveCall((previous) => previous ? { ...previous, status: "accepted" } : previous);
          return;
        }

        if (["declined", "cancelled", "expired", "ended"].includes(eventStatus)) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          activeCallRef.current = null;
          setActiveCall(null);
          setCallState("idle");
        }
      },
    });

    return () => {
      ably?.close?.();
    };
  }, [canUseLauncher, pharmacistIdForSelectedPharmacy, pharmacyId]);

  useEffect(() => {
    if (!activeCall?.callId) {
      sessionEndSubscriptionRef.current?.close?.();
      sessionEndSubscriptionRef.current = null;
      return undefined;
    }

    const subscription = subscribeToPharmacistDriverVideoCallSession(
      activeCall.callId,
      () => {
        activeCallRef.current = null;
        setActiveCall(null);
        setCallState("idle");
      },
      { pharmacyId },
    );

    sessionEndSubscriptionRef.current = subscription;

    return () => {
      subscription?.close?.();
      if (sessionEndSubscriptionRef.current === subscription) {
        sessionEndSubscriptionRef.current = null;
      }
    };
  }, [activeCall?.callId, pharmacyId]);

  useEffect(() => {
    const endActiveCallBeforePageLeaves = () => {
      const current = activeCallRef.current;
      if (!current?.callId || unloadCleanupSentRef.current) return;
      if (!["requesting", "accepted"].includes(callStateRef.current)) return;

      unloadCleanupSentRef.current = true;
      endPharmacistDriverVideoCallFromDeliveryAppOnUnload(current.callId);
    };

    window.addEventListener("pagehide", endActiveCallBeforePageLeaves);
    window.addEventListener("beforeunload", endActiveCallBeforePageLeaves);

    return () => {
      window.removeEventListener("pagehide", endActiveCallBeforePageLeaves);
      window.removeEventListener("beforeunload", endActiveCallBeforePageLeaves);
    };
  }, []);

  const startCall = async () => {
    if (!selectedDriverId || starting || !canUseLauncher) return;

    setStarting(true);
    setNotice("");

    try {
      const selectedDriver = drivers.find((driver) => String(driver.id) === String(selectedDriverId));
      const shouldUseNativeHandoff = isFlutterNativeShell();
      const response = normalizeCallData(await initiatePharmacistDriverVideoCallFromDeliveryApp({
        driverId: selectedDriverId,
        pharmacyId,
        pharmacistId: pharmacistIdForSelectedPharmacy,
        deferNotify: shouldUseNativeHandoff,
      }));

      const nextCall = {
        ...response,
        driverName: response?.driverName || selectedDriver?.name || "Assistant",
        pharmacistName: response?.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist",
      };

      if (shouldUseNativeHandoff) {
        const handedOff = postToFlutter({
          type: "pharmacistDriverOutgoingCallStartNative",
          authToken: Cookies.get("authToken") || null,
          ablyApiKey: ablyApiKey || null,
          pharmacyId: String(nextCall.pharmacyId || pharmacyId || ""),
          driverID: String(nextCall.driverId || selectedDriverId || ""),
          driverId: String(nextCall.driverId || selectedDriverId || ""),
          driverName: nextCall.driverName || "Assistant",
          pharmacistId: String(nextCall.pharmacistId || pharmacistIdForSelectedPharmacy || ""),
          pharmacistName: nextCall.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist",
          initiateData: {
            ...nextCall,
            call_id: nextCall.callId,
            callId: nextCall.callId,
            pharmacy_id: nextCall.pharmacyId || pharmacyId,
            driver_id: nextCall.driverId || selectedDriverId,
            driverName: nextCall.driverName || "Assistant",
            driver_name: nextCall.driverName || "Assistant",
            pharmacist_id: nextCall.pharmacistId || pharmacistIdForSelectedPharmacy,
            pharmacistName: nextCall.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist",
            pharmacist_name: nextCall.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist",
            flow: "pharmacist_to_driver",
            type: "pharmacist_driver_video_call_request",
            pharmacistDriverFlow: true,
            driver: {
              id: nextCall.driverId || selectedDriverId,
              name: nextCall.driverName || "Assistant",
            },
            pharmacist: {
              id: nextCall.pharmacistId || pharmacistIdForSelectedPharmacy,
              name: nextCall.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist",
            },
          },
        });
        if (handedOff) {
          setIsPickerOpen(false);
          setStarting(false);
          return;
        }
      }

      setActiveCall(nextCall);
      setCallState("requesting");
      setIsPickerOpen(false);

      timeoutRef.current = window.setTimeout(() => {
        const current = activeCallRef.current;
        if (!current?.callId || callStateRef.current === "accepted") return;

        cancelPharmacistDriverVideoCallFromDeliveryApp(current.callId, "timeout").catch(() => {});
        activeCallRef.current = null;
        setActiveCall(null);
        setCallState("idle");
      }, RING_TIMEOUT_MS);
    } catch (error) {
      setNotice(error?.response?.data?.message || "Unable to start the video call.");
    } finally {
      setStarting(false);
    }
  };

  const closeActiveCall = useCallback(() => {
    const current = activeCallRef.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (current?.callId) {
      if (callStateRef.current === "requesting") {
        cancelPharmacistDriverVideoCallFromDeliveryApp(current.callId, "pharmacist_cancelled").catch(() => {});
      } else if (callStateRef.current === "accepted") {
        endPharmacistDriverVideoCallFromDeliveryApp(current.callId).catch(() => {});
      }
    }

    activeCallRef.current = null;
    setActiveCall(null);
    setCallState("idle");
  }, []);

  const handleScreenshot = useCallback((blob) => {
    if (!blob) return;
    const imageUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `pharmacist-assistant-video-call-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(imageUrl), 1000);
  }, []);

  const filteredDrivers = useMemo(() => {
    const needle = driverSearch.trim().toLowerCase();
    if (!needle) return drivers;
    return drivers.filter((driver) => `${driver.name} ${driver.email}`.toLowerCase().includes(needle));
  }, [driverSearch, drivers]);

  if (!canUseLauncher) return null;

  const renderedTrigger = typeof trigger === "function"
    ? trigger({ openPicker, disabled: !!activeCall || starting })
    : null;

  return (
    <>
      {renderedTrigger}

      <AnimatedBottomSheet
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        fullScreen
        ariaLabel="Call Assistant"
      >
            <div className="relative flex h-full max-h-[calc(100dvh-12px)] min-h-0 flex-col overflow-hidden rounded-t-[28px] bg-white px-5 pt-2">
              <div className="shrink-0">
                <div className="mb-5 flex items-center justify-center pt-1">
                  <div className="h-1 w-12 rounded-full bg-gray-300" />
                </div>

                <div className="mb-4">
                  <p className="text-xl font-bold text-gray-950">Call Assistant</p>
                  <p className="mt-1 text-sm text-gray-500">Select an assistant to start a video call.</p>
                </div>

                <div className="mb-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <input
                    value={driverSearch}
                    onChange={(event) => setDriverSearch(event.target.value)}
                    placeholder="Search assistants"
                    className="w-full border-0 bg-transparent text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {loadingDrivers ? (
                  <div className="flex justify-center h-96">
                    <LoadingView />
                  </div>
                ) : filteredDrivers.length === 0 ? (
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm font-semibold text-gray-500">
                    No assistants available
                  </div>
                ) : (
                  filteredDrivers.map((driver) => {
                    const selected = String(selectedDriverId) === String(driver.id);

                    return (
                      <button
                        key={driver.id}
                        type="button"
                        onClick={() => setSelectedDriverId(driver.id)}
                        className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                          selected
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-200 bg-white hover:border-blue-200"
                        }`}
                      >
                        <img
                          src={driver.photo || fallbackAvatar}
                          alt=""
                          className="h-11 w-11 shrink-0 rounded-xl object-cover"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = fallbackAvatar;
                          }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-gray-950">{driver.name}</span>
                          {driver.email ? <span className="block truncate text-xs text-gray-500">{driver.email}</span> : null}
                        </span>
                        <span className={`h-4 w-4 rounded-full border ${
                          selected ? "border-blue-600 bg-blue-600 ring-4 ring-blue-100" : "border-gray-300 bg-white"
                        }`} />
                      </button>
                    );
                  })
                )}
              </div>

              {notice ? (
                <div className="mt-3 shrink-0 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {notice}
                </div>
              ) : null}

              <div
                className="mt-4 flex shrink-0 gap-3 border-t border-gray-100 bg-white pt-4"
                style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
              >
                <button
                  type="button"
                  onClick={() => setIsPickerOpen(false)}
                  className="flex-1 rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-bold text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={startCall}
                  disabled={!selectedDriverId || starting}
                  className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-md disabled:bg-gray-300"
                >
                  {starting ? "Calling..." : "Start Call"}
                </button>
              </div>
            </div>
      </AnimatedBottomSheet>

      {activeCall ? (
        <VideoCallErrorBoundary onClose={closeActiveCall}>
          <VideoCall
            isOpen={!!activeCall}
            onClose={closeActiveCall}
            patientData={{ first_name: activeCall.driverName || "Assistant", last_name: "" }}
            driverName={activeCall.driverName || "Assistant"}
            pharmacistName={activeCall.pharmacistName || pharmacistNameForSelectedPharmacy || "Pharmacist"}
            callState={callState}
            isCaller={true}
            vonageSessionId={activeCall.vonage_session_id}
            vonageToken={activeCall.vonage_token}
            applicationId={activeCall.application_id}
            videoProvider={activeCall.video_provider}
            livekitUrl={activeCall.livekit_url}
            onScreenshot={handleScreenshot}
            callMode="pharmacist_to_driver"
          />
        </VideoCallErrorBoundary>
      ) : null}
    </>
  );
};

export default PharmacistDriverCallLauncher;
